import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiDownload,
  FiSearch,
  FiShoppingCart,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const supplierName = (item) => {
  const suppliers = item.variant?.product?.suppliers || [];

  const preferred = suppliers.find((supplier) => supplier.isPreferred);

  return (
    preferred?.supplier?.displayName ||
    preferred?.supplier?.name ||
    suppliers[0]?.supplier?.displayName ||
    suppliers[0]?.supplier?.name ||
    "—"
  );
};

const getStatus = (item) => {
  const available = number(item.availableQuantity);
  const minimumStock = item.minimumStock;

  if (available <= 0) return "Critical";

  if (
    minimumStock !== null &&
    minimumStock !== undefined &&
    available <= number(minimumStock)
  ) {
    return "Critical";
  }

  return "Low";
};

export default function LowStockReportPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [storeId, setStoreId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const response = await apiRequest("/inventory/reports/low-stock", {
        accessToken,
      });

      setItems(response || []);
    } catch (err) {
      setError(err.message || "Unable to load low stock report.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadReport();
  }, [isAuthLoading, accessToken, loadReport]);

  const stores = useMemo(() => {
    const uniqueStores = new Map();

    items.forEach((item) => {
      if (item.store) {
        uniqueStores.set(item.store.id, item.store);
      }
    });

    return [...uniqueStores.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [items]);

  const categories = useMemo(() => {
    const uniqueCategories = new Map();

    items.forEach((item) => {
      const category = item.variant?.product?.category;

      if (category) {
        uniqueCategories.set(category.id, category);
      }
    });

    return [...uniqueCategories.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter((item) => {
      const productName =
        item.variant?.product?.name?.toLowerCase() || "";

      const sku = item.variant?.sku?.toLowerCase() || "";

      return (
        (!term || productName.includes(term) || sku.includes(term)) &&
        (!storeId || item.store?.id === storeId) &&
        (!categoryId ||
          item.variant?.product?.category?.id === categoryId) &&
        (!status || getStatus(item) === status)
      );
    });
  }, [items, search, storeId, categoryId, status]);

  const summary = useMemo(() => {
    const critical = items.filter(
      (item) => getStatus(item) === "Critical",
    ).length;

    const affectedStores = new Set(
      items.map((item) => item.store?.id).filter(Boolean),
    ).size;

    return {
      critical,
      low: items.length - critical,
      affectedStores,
      restockNeeded: items.length,
    };
  }, [items]);

  const exportCsv = () => {
    const headers = [
      "Product",
      "SKU",
      "Category",
      "Store",
      "Available Stock",
      "Reorder Level",
      "Supplier",
      "Status",
    ];

    const rows = filteredItems.map((item) => [
      item.variant?.product?.name || "",
      item.variant?.sku || "",
      item.variant?.product?.category?.name || "—",
      item.store?.name || "—",
      item.availableQuantity,
      item.reorderLevel,
      supplierName(item),
      getStatus(item),
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `low-stock-report-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">Inventory / Reports</p>

          <h1 className="mt-1 text-3xl font-bold">
            Low Stock Report
          </h1>

          <p className="mt-2 text-gray-500">
            Monitor variants at or below their configured reorder level.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportCsv}
            disabled={filteredItems.length === 0}
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 disabled:opacity-60"
          >
            <FiDownload />
            Export
          </button>

          <button
            disabled
            title="Purchase Orders will be enabled after the Purchases module is built."
            className="flex h-11 cursor-not-allowed items-center gap-2 rounded-xl bg-primary-700 px-5 text-white opacity-60"
          >
            <FiShoppingCart />
            Create PO
          </button>
        </div>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard
          title="Critical Items"
          value={summary.critical}
          color="red"
        />

        <StatCard
          title="Low Stock Items"
          value={summary.low}
          color="orange"
        />

        <StatCard
          title="Stores Affected"
          value={summary.affectedStores}
          color="blue"
        />

        <StatCard
          title="Restock Needed"
          value={summary.restockNeeded}
          color="green"
        />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product or SKU..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Stores</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>

          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Categories</option>

            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Status</option>
            <option value="Critical">Critical</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {[
                  "Product",
                  "Category",
                  "Store",
                  "Stock",
                  "Reorder",
                  "Supplier",
                  "Status",
                  "Action",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-4 text-left text-xs uppercase text-gray-500 last:text-right"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading low stock report…
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No low-stock alerts found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const itemStatus = getStatus(item);

                  return (
                    <tr
                      key={item.settingId}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <h4 className="font-semibold">
                          {item.variant?.product?.name}
                        </h4>

                        <p className="mt-1 text-xs text-gray-500">
                          {item.variant?.sku}
                        </p>
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {item.variant?.product?.category?.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {item.store?.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-center font-bold text-red-600">
                        {quantity(item.availableQuantity)}
                      </td>

                      <td className="px-6 py-4 text-center">
                        {quantity(item.reorderLevel)}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {supplierName(item)}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${itemStatus === "Critical"
                              ? "bg-red-100 text-red-700"
                              : "bg-orange-100 text-orange-700"
                            }`}
                        >
                          {itemStatus}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right">
                        <button
                          disabled
                          title="Available after the Purchases module is built."
                          className="rounded-lg bg-primary-700 px-4 py-2 text-sm text-white opacity-60"
                        >
                          Create PO
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color }) {
  const colors = {
    red: "bg-red-50 text-red-600",
    orange: "bg-orange-50 text-orange-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div
        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${colors[color]}`}
      >
        <FiAlertTriangle size={22} />
      </div>

      <p className="text-sm text-gray-500">{title}</p>

      <h3 className="mt-2 text-3xl font-bold">{value}</h3>
    </div>
  );
}